// The exercise editor on the exercise settings page (#6428): the Advanced fold's Intensity choice,
// Next Session Only block, Strength at Last Deload, the waiting plateau and volume questions, Set
// Aside and the list of recent changes. The page builds one of these only when the editor is shown,
// and binds its template to `view`. Rules: design-docs/coach-exercise-editing.md

import { LL } from 'i18n/i18n.service';
import { CoachExerciseEditResult } from './exercise-configuration-interface';
import { FitnessPlan, pathAtLevel, setAsideExerciseIdOf, getRepTargetForIntensity, getSlotPathIntensity, IntensityLevel, Plan_ExercisePath, Plan_SubSlot } from '@cf-shared/data-model/fitness-plan';
import { FitnessState, ExerciseStat, QUARANTINE_DAYS } from '@cf-shared/data-model/user-state';
import { Exercise, ExerciseDbService } from 'services/exercise-db.service';
import { DocWatcher } from 'services/database-wrapper';
import { UserDb } from 'services/user-db.service';
import { DebugService } from 'services/debug.service';
import { deepClone } from '@cf-shared/deep-helpers';
import { SettingsService } from 'services/settings.service';
import { TranslatedString } from 'models/translated-string';
import { LoggingService } from 'services/logging.service';
import { isDatabaseTornDown } from 'services/database-unavailable-error';
import { describeErrorForLog } from '@cf-shared/error-describe';
import { DateTime } from 'luxon';
import { ExerciseChangeDoc } from '@cf-shared/data-model/exercise-change';
import { EquipmentType, Equipment, EquipmentManual } from '@cf-shared/data-model/equipment';
import { SetStats } from '@cf-shared/data-model/set-stats';
import { formatMeasure } from '@cf-shared/measure-format';
import { CoachExerciseChangeService, RecentExerciseHistory } from 'services/coach-exercise-change.service';
import { getEquipmentForExercise } from 'services/equipment-helpers';
import { formatShortDate } from 'util/formatting';
import { bestStrengthSetAcrossSessions } from 'pages/workout-detail/workout-progression-decision';
import { AUTOREGULATION_FIELDS, clearAutoregulationFields } from 'pages/workout-detail/autoregulation-resolve';
import { buildSchemeSwitch, IntensityScheme, levelsOfScheme, schemeOfLevel, schemeOfPaths, schemesForTrainingLevel } from './scheme-switch';
import { buildChangeRows, ChangeRow, dayLabel, schemeLabel } from './coach-exercise-view';

/**
 * What the editor shares with the page. The documents are the page's drafts and the copies taken
 * when it opened; the page sets them up once and never replaces them, so holding them here is safe.
 */
export interface ExerciseStateEditorContext {
  fitnessPlan: FitnessPlan;
  fitnessState: FitnessState;
  fitnessPlanInitial: FitnessPlan;
  fitnessStateInitial: FitnessState;
  subSlotId: string;
  exerciseId: string;
  exercise: Exercise;
  exerciseName: string;
  /** the Sets count the page showed when it opened: a Volume change is judged against it */
  initialNumSets: number;
  /** the Sets count the page shows now */
  numSets: () => number;
  docWatcher: DocWatcher;
  /** rebuilds the page's rep-target rows from the sub-slot, after a scheme switch changed its days */
  reloadRepTargets: (subSlot: Plan_SubSlot) => void;
  markForCheck: () => void;
}

export interface ExerciseStateEditorServices {
  userDb: UserDb;
  exerciseDb: ExerciseDbService;
  debug: DebugService;
  logging: LoggingService;
  settings: SettingsService;
  coachExerciseChange: CoachExerciseChangeService;
}

export class ExerciseStateEditor {
  /** what the editor's parts of the page show; set by `loadAsync` */
  view: CoachView | undefined;
  /** true when a scheme switch (or Calculate Weights) built the paths in this visit */
  private pathsRebuiltBySwitch = false;
  /** the days whose Next Weight the coach typed; a rep-target change after a switch leaves them alone */
  private weightTypedByHand = new Set<IntensityLevel>();
  private history: RecentExerciseHistory | undefined;
  private historyUnreadable = false;
  private exerciseChanges: ExerciseChangeDoc[] = [];
  /** the name of the exercise the app set aside from this slot, when one is recorded */
  private setAsideExerciseName: string | undefined;

  constructor(
    private ctx: ExerciseStateEditorContext,
    private services: ExerciseStateEditorServices,
  ) {}

  /**
   * Reads what the coach's parts need: the exercise's recent workouts and the coach's earlier
   * changes. A failed history read is never taken as "no history": the intensity switch is
   * disabled and the page says why. A failed read of the earlier changes only empties that list.
   */
  async loadAsync() {
    const { logging } = this.services;
    try {
      this.history = await this.services.coachExerciseChange.readHistoryAsync(this.ctx.exerciseId, this.ctx.fitnessPlan);
    } catch (err) {
      if (!isDatabaseTornDown(err)) {
        logging.trackSilentProblem('exercise-configuration.coach-history-unreadable', { subSlotId: this.ctx.subSlotId, error: describeErrorForLog(err) });
      }
      this.historyUnreadable = true;
    }
    try {
      this.exerciseChanges = await this.services.userDb.getExerciseChangesForSubSlotAsync(this.ctx.subSlotId, 20);
    } catch (err) {
      if (!isDatabaseTornDown(err)) {
        logging.trackSilentProblem('exercise-configuration.coach-changes-unreadable', { subSlotId: this.ctx.subSlotId, error: describeErrorForLog(err) });
      }
      this.exerciseChanges = [];
    }
    const setAsideId = this.setAsideCandidateId();
    if (setAsideId && setAsideId !== this.ctx.exerciseId) {
      this.setAsideExerciseName = await this.services.exerciseDb.getExerciseNameByIdAsync(setAsideId, this.ctx.docWatcher);
    }
    this.refreshView();
  }

  /** the coach's sub-slot result, handed back only when something changed */
  editResult(somethingChanged: boolean): CoachExerciseEditResult | undefined {
    if (!this.view || !somethingChanged) return undefined;
    const numSets = this.ctx.numSets();
    const volume = numSets !== this.ctx.initialNumSets ? { before: this.ctx.initialNumSets, after: numSets } : undefined;
    return { subSlotId: this.ctx.subSlotId, pathsRebuiltBySwitch: this.pathsRebuiltBySwitch, volume };
  }

  /** a Min Reps error the coach has not fixed yet blocks Save, like a rep-target error */
  get hasValidationError(): boolean {
    return !!this.view?.nextRows.some(row => !!row.minRepsError);
  }

  /** the page's Sets count changed: a Volume change answers a waiting volume question */
  onNumSetsChanged() {
    // the client's app clears the card; the coach's device writes only the plan
    // (design-docs/coach-exercise-editing.md)
    this.syncOpenVolumeQuestion();
    this.refreshView();
  }

  /** the page wrote a rep target: right after a switch the weights follow it, except a weight the coach typed */
  onRepTargetChanged() {
    if (this.pathsRebuiltBySwitch) {
      this.recalculateSwitchedWeights();
    }
    this.refreshView();
  }

  private get subSlot(): Plan_SubSlot {
    return this.ctx.fitnessPlan.subSlots[this.ctx.subSlotId];
  }

  private get initialSubSlot(): Plan_SubSlot | undefined {
    return this.ctx.fitnessPlanInitial?.subSlots?.[this.ctx.subSlotId];
  }

  /** a weight in the reader's unit */
  private formatWeight = (kg: number): string =>
    formatMeasure(kg, 'addedweight_kg', this.services.settings.locale, this.ctx.fitnessState.exerciseStats[this.ctx.exerciseId]?.weight_unit);

  /** a date in the reader's short format, or empty for a missing or unreadable one */
  private formatDate(iso: string | undefined): string {
    const time = iso ? DateTime.fromISO(iso) : undefined;
    return time?.isValid ? formatShortDate(time.toLocal(), this.services.settings) : '';
  }

  /** the time a `log/<utc>-Workout` id was written, read off the id itself */
  private workoutIdTime(id: string | undefined): string | undefined {
    if (typeof id !== 'string' || !id.startsWith('log/')) return undefined;
    const end = id.indexOf('-Workout');
    return end > 4 ? id.slice(4, end) : undefined;
  }

  /** the client's best recent set, the one the weights of a switch are worked out from (#5176's reading) */
  private bestRecentSet(): { stats: SetStats; time_utc: string | undefined } | undefined {
    if (!this.history) return undefined;
    const best = bestStrengthSetAcrossSessions(this.history.sessionsNewestFirst, this.ctx.fitnessState.weight_kg, this.ctx.exercise?.bw_pc || 0);
    if (!best) return undefined;
    const index = this.history.sessionsNewestFirst.findIndex(sets => sets.some(set => set?.stats === best));
    return { stats: best, time_utc: index >= 0 ? this.history.workouts[index]?.time_utc : undefined };
  }

  /** the client's equipment for this exercise; a 2.5 kg step when none is set up */
  private equipmentForWeights(): Equipment {
    const stat = this.ctx.fitnessState.exerciseStats[this.ctx.exerciseId];
    // a copy: the lookup can migrate a legacy field on the stat, which must not read as an edit here
    const equipment = getEquipmentForExercise(this.ctx.fitnessState, deepClone(stat), this.services.settings.locale);
    return equipment ?? { id: 'coach-default', name: '', type: EquipmentType.Manual, weight_min_kg: 0, weight_inc_kg: 2.5 } as EquipmentManual;
  }

  /** the rep target a day of a new scheme starts with: the client's stated preference, else the app's default */
  private defaultRepTarget(level: IntensityLevel): number {
    const prefer = this.ctx.fitnessState.exerciseStats[this.ctx.exerciseId]?.preferRepTargetByIntensity?.[level];
    return prefer ?? getRepTargetForIntensity(getSlotPathIntensity(this.subSlot, { intensityLevel: level } as Plan_ExercisePath));
  }

  /**
   * The coach picked a scheme (or Calculate Weights while a strength test is pending): build the
   * new days from the best recent set, or fall back to a strength test when nothing can be worked
   * out. Either way the waiting plateau question is answered by the switch.
   */
  handleSchemeChange(scheme: IntensityScheme) {
    if (!this.view || this.historyUnreadable) return;
    const subSlot = this.subSlot;
    const levels = levelsOfScheme(scheme, subSlot.exercisePaths);
    const repTargetsByLevel: { [level: number]: number } = {};
    for (const level of levels) {
      // a day that exists already keeps its rep target
      repTargetsByLevel[level] = pathAtLevel(subSlot, level)?.target_reps ?? this.defaultRepTarget(level);
    }
    this.applySchemeSwitch(scheme, levels, repTargetsByLevel);
    this.weightTypedByHand.clear();
    // the switch answers a waiting plateau question; the client's app clears the card
    delete subSlot.plateauWorkoutLog_id;
    this.ctx.reloadRepTargets(subSlot);
    this.refreshView();
    this.ctx.markForCheck();
  }

  /**
   * Builds the days of a scheme onto the draft (or sets a strength test when they cannot be worked
   * out), records the rep targets as the client's stated preference, as a rep-target edit does, and
   * marks that the paths were rebuilt, so the save step never applies the deload rule to them.
   */
  private applySchemeSwitch(scheme: IntensityScheme, levels: IntensityLevel[], repTargetsByLevel: { [level: number]: number }) {
    const subSlot = this.subSlot;
    const intensityPcByLevel: { [level: number]: number | undefined } = {};
    for (const level of levels) {
      intensityPcByLevel[level] = getSlotPathIntensity(subSlot, { intensityLevel: level } as Plan_ExercisePath);
    }
    const result = buildSchemeSwitch({
      scheme,
      levels,
      repTargetsByLevel,
      intensityPcByLevel,
      bestRecentSet: this.bestRecentSet()?.stats,
      bodyWeight_kg: this.ctx.fitnessState.weight_kg,
      exerciseBw_pc: this.ctx.exercise?.bw_pc || 0,
      equipment: this.equipmentForWeights(),
    });
    if (result.kind === 'calculated') {
      subSlot.exercisePaths = result.paths;
      subSlot.nextWorkoutIntensity = result.nextWorkoutIntensity;
      delete subSlot.rebuildPathsForIntensity;
    } else {
      // today's route: the next load rebuilds the paths and starts with a strength test
      subSlot.rebuildPathsForIntensity = result.rebuildPathsForIntensity;
    }
    // the rep targets are the coach's stated preference, as a rep-target edit records them
    const exerciseStats = this.ctx.fitnessState.exerciseStats;
    const stat = exerciseStats[this.ctx.exerciseId] || {} as ExerciseStat;
    stat.preferRepTargetByIntensity ||= {};
    for (const level of levels) {
      stat.preferRepTargetByIntensity[level] = repTargetsByLevel[level];
    }
    exerciseStats[this.ctx.exerciseId] = stat;
    this.pathsRebuiltBySwitch = true;
  }

  /** after a switch, a changed rep target moves that day's weight too, unless the coach typed it */
  private recalculateSwitchedWeights() {
    const subSlot = this.subSlot;
    if (subSlot.rebuildPathsForIntensity !== undefined) return;
    const scheme = schemeOfPaths(subSlot.exercisePaths);
    const levels = (subSlot.exercisePaths ?? []).map(path => path.intensityLevel);
    const typed = new Map((subSlot.exercisePaths ?? [])
      .filter(path => this.weightTypedByHand.has(path.intensityLevel))
      .map(path => [path.intensityLevel, path.target_weight_kg]));
    const repTargetsByLevel: { [level: number]: number } = {};
    for (const path of subSlot.exercisePaths ?? []) {
      repTargetsByLevel[path.intensityLevel] = path.target_reps;
    }
    this.applySchemeSwitch(scheme, levels, repTargetsByLevel);
    for (const path of subSlot.exercisePaths ?? []) {
      if (typed.has(path.intensityLevel)) {
        path.target_weight_kg = typed.get(path.intensityLevel);
        path.orgTarget_weight_kg = path.target_weight_kg;
      }
    }
  }

  /** Calculate Weights, shown while a strength test is pending: work the pending scheme's weights out now */
  handleCalculateWeights() {
    const pending = this.subSlot.rebuildPathsForIntensity;
    if (pending === undefined) return;
    this.handleSchemeChange(schemeOfLevel(pending));
  }

  /**
   * The coach typed a Next Weight. On a path a switch just built it is also the starting weight; on
   * an existing path the save step applies the deload rule instead (coach-exercise-change.ts).
   */
  handleNextWeightChange(row: NextSessionRow) {
    const path = pathAtLevel(this.subSlot, row.level);
    if (!path || row.weight_kg === undefined || !(row.weight_kg >= 0)) {
      this.refreshView();
      return;
    }
    path.target_weight_kg = row.weight_kg;
    if (this.pathsRebuiltBySwitch) {
      path.orgTarget_weight_kg = row.weight_kg;
    }
    this.weightTypedByHand.add(row.level);
    this.refreshView();
    this.ctx.markForCheck();
  }

  /** Next Minimum Reps: a whole number of at least 1, or empty. No upper bound: above the rep target is legitimate */
  handleNextMinRepsChange(row: NextSessionRow) {
    const path = pathAtLevel(this.subSlot, row.level);
    if (!path) return;
    const value = row.minReps;
    if (value === undefined || value === null || (value as unknown) === '') {
      delete path.min_reps;
      row.minRepsError = undefined;
    } else if (!Number.isInteger(value) || value < 1) {
      row.minRepsError = LL.exerciseConfiguration.coach.minRepsInvalid();
      this.ctx.markForCheck();
      return;
    } else {
      path.min_reps = value;
      row.minRepsError = undefined;
    }
    this.refreshView();
    this.ctx.markForCheck();
  }

  /** the exercise a set-aside record names, or the current one when it is itself quarantined (rare) */
  private setAsideCandidateId(): string | undefined {
    const now = this.services.debug.getNowLocal().toUTC();
    const recorded = setAsideExerciseIdOf(this.initialSubSlot);
    const initialStats = this.ctx.fitnessStateInitial?.exerciseStats ?? {};
    if (recorded && ExerciseStat.isQuarantined(initialStats[recorded], now)) return recorded;
    if (ExerciseStat.isQuarantined(initialStats[this.ctx.exerciseId], now)) return this.ctx.exerciseId;
    return undefined;
  }

  /** Bring Back Now: clears the set-aside exercise's quarantine in the draft; it does not swap the exercise in */
  handleBringBackNow() {
    const id = this.setAsideCandidateId();
    const stat = id ? this.ctx.fitnessState.exerciseStats[id] : undefined;
    if (!stat) return;
    ExerciseStat.unmarkQuarantined(stat);
    this.refreshView();
    this.ctx.markForCheck();
  }

  /**
   * A Volume change answers the volume question that was open when the page opened (R6). Only a real
   * change does: `editDone` also fires when the field loses focus unchanged, and then the question
   * stays open, with the fields as the page opened them.
   */
  private syncOpenVolumeQuestion() {
    const changed = this.ctx.numSets() !== this.ctx.initialNumSets;
    for (const path of this.subSlot.exercisePaths ?? []) {
      if (!path) continue;
      // a path a switch built starts fresh, with no question of its own to keep or answer
      if (changed || this.pathsRebuiltBySwitch) {
        clearAutoregulationFields(path);
        continue;
      }
      const opened = pathAtLevel(this.initialSubSlot, path.intensityLevel);
      for (const field of AUTOREGULATION_FIELDS) {
        if (opened?.[field] !== undefined) {
          (path as unknown as Record<string, unknown>)[field] = opened[field];
        }
      }
    }
  }

  /** Rebuilds everything the coach's parts show from the drafts. */
  private refreshView() {
    const subSlot = this.subSlot;
    const pendingLevel = subSlot.rebuildPathsForIntensity;
    const scheme = pendingLevel !== undefined ? schemeOfLevel(pendingLevel) : schemeOfPaths(subSlot.exercisePaths);
    const allowed = schemesForTrainingLevel(subSlot.trainingLevel);
    const schemes = allowed.includes(scheme) ? allowed : [...allowed, scheme];
    // the day that comes next first, then heaviest first
    const nextLevel = subSlot.nextWorkoutIntensity;
    const paths = [...(subSlot.exercisePaths ?? [])].filter(path => !!path)
      .sort((a, b) => (a.intensityLevel === nextLevel ? -1 : b.intensityLevel === nextLevel ? 1 : b.intensityLevel - a.intensityLevel));
    const changeRows: ChangeRow[] = buildChangeRows(this.exerciseChanges, this.history?.workouts ?? [], this.ctx.exerciseId, this.formatWeight);
    this.view = {
      schemeOptions: schemes.map(s => ({ scheme: s, label: schemeLabel(s) })),
      scheme,
      pendingTest: pendingLevel !== undefined,
      historyUnreadable: this.historyUnreadable,
      nextRows: this.buildNextRows(paths, nextLevel),
      sourceNote: this.buildSourceNote(nextLevel),
      strengthRows: this.buildStrengthRows(paths),
      questionCards: this.buildQuestionCards(),
      setAside: this.buildSetAside(),
      changes: changeRows.map(row => ({
        date: formatShortDate(row.time.toLocal(), this.services.settings),
        text: LL.exerciseConfiguration.coach.changeBy({ who: row.who, what: row.what }),
      })),
    };
  }

  /** the Next Session Only rows; a one-target exercise has one day, so it needs no day line */
  private buildNextRows(paths: Plan_ExercisePath[], nextLevel: IntensityLevel | undefined): NextSessionRow[] {
    const previousRows = new Map((this.view?.nextRows ?? []).map(row => [row.level, row]));
    return paths.map(path => ({
      level: path.intensityLevel,
      label: paths.length === 1 ? undefined : dayLabel(path.intensityLevel),
      // the day that comes next says so in a tooltip beside its name
      comesNextTooltip: paths.length > 1 && path.intensityLevel === nextLevel
        ? LL.exerciseConfiguration.coach.comesNext({ exercise: this.ctx.exerciseName }) : undefined,
      weight_kg: path.target_weight_kg,
      minReps: path.min_reps,
      // an error stays until the coach fixes the field
      minRepsError: previousRows.get(path.intensityLevel)?.minRepsError,
    }));
  }

  /** where the next weights come from: the best recent set right after a switch, else the last session */
  private buildSourceNote(nextLevel: IntensityLevel | undefined): TranslatedString | undefined {
    const coachLL = LL.exerciseConfiguration.coach;
    const best = this.bestRecentSet();
    if (this.pathsRebuiltBySwitch && this.subSlot.rebuildPathsForIntensity === undefined && best) {
      return coachLL.calculatedFrom({ weight: this.formatWeight(best.stats.weight_kg), reps: best.stats.reps, date: this.formatDate(best.time_utc) });
    }
    const lastStats = pathAtLevel(this.subSlot, nextLevel)?.lastStats;
    if (lastStats && Number.isFinite(lastStats.weight_kg) && Number.isFinite(lastStats.reps)) {
      return coachLL.lastSession({ weight: this.formatWeight(lastStats.weight_kg), reps: lastStats.reps, date: this.formatDate(this.subSlot.lastTimeConcluded_utc) });
    }
    return undefined;
  }

  /** Strength at Last Deload per day (read-only); one "None yet." when no day has one */
  private buildStrengthRows(paths: Plan_ExercisePath[]): CoachView['strengthRows'] {
    const coachLL = LL.exerciseConfiguration.coach;
    const hasStrength = (path: Plan_ExercisePath) => typeof path.lastDeload1rm_net_kg === 'number' && Number.isFinite(path.lastDeload1rm_net_kg);
    if (!paths.some(hasStrength)) {
      return [{ day: undefined, text: coachLL.deloadStrengthNone() }];
    }
    return paths.map(path => ({
      day: paths.length === 1 ? undefined : dayLabel(path.intensityLevel),
      text: hasStrength(path)
        ? coachLL.deloadStrength({ strength: this.formatWeight(path.lastDeload1rm_net_kg), limit: this.formatWeight(path.lastDeload1rm_net_kg * 1.05) })
        : coachLL.deloadStrengthNone(),
    }));
  }

  /**
   * The waiting plateau and volume questions, and whether saving answers them. A user on their own
   * program is not told that someone else has not answered.
   */
  private buildQuestionCards(): CoachView['questionCards'] {
    const coachLL = LL.exerciseConfiguration.coach;
    const managing = this.services.userDb.getCoachManagingClient();
    const client = managing?.clientDisplayName ?? '';
    const initial = this.initialSubSlot;
    const cards: CoachView['questionCards'] = [];

    if (initial?.plateauWorkoutLog_id) {
      const date = this.formatDate(this.workoutIdTime(initial.plateauWorkoutLog_id));
      if (!this.subSlot.plateauWorkoutLog_id) {
        cards.push({ type: 'success', message: managing ? coachLL.plateauWillAnswer({ date, client }) : coachLL.plateauWillAnswerSelf({ date }) });
      } else {
        cards.push({ type: 'warning', message: managing ? coachLL.plateauWaiting({ date, client }) : coachLL.plateauWaitingSelf({ date }) });
      }
    }

    const openVolumeId = (s: Plan_SubSlot | undefined) =>
      s?.exercisePaths?.find(path => typeof path?.autoregulationWorkoutLog_id === 'string')?.autoregulationWorkoutLog_id;
    const volumeId = openVolumeId(initial);
    if (volumeId) {
      const date = this.formatDate(this.workoutIdTime(volumeId));
      if (openVolumeId(this.subSlot)) {
        cards.push({ type: 'warning', message: managing ? coachLL.volumeWaiting({ date, client }) : coachLL.volumeWaitingSelf({ date }) });
      } else {
        cards.push({ type: 'success', message: managing ? coachLL.volumeWillAnswer({ date, client }) : coachLL.volumeWillAnswerSelf({ date }) });
      }
    }
    return cards;
  }

  /** the Set Aside line, while the set-aside exercise (or, rarely, the current one) is still quarantined */
  private buildSetAside(): CoachView['setAside'] {
    const coachLL = LL.exerciseConfiguration.coach;
    const setAsideId = this.setAsideCandidateId();
    if (!setAsideId) return undefined;
    const initialStat = this.ctx.fitnessStateInitial.exerciseStats[setAsideId];
    const since = initialStat?.quarantined_utc;
    const until = since ? DateTime.fromISO(since).plus({ days: QUARANTINE_DAYS }).toISO() : undefined;
    const name = setAsideId === this.ctx.exerciseId ? this.ctx.exerciseName : (this.setAsideExerciseName ?? '');
    const broughtBack = !ExerciseStat.hasQuarantineRecorded(this.ctx.fitnessState.exerciseStats[setAsideId]);
    if (broughtBack) {
      return { text: coachLL.broughtBack({ exercise: name }), canBringBack: false };
    }
    const params = { exercise: name, date: this.formatDate(since), until: this.formatDate(until) };
    return {
      text: initialStat?.quarantineReason === 'weakness' ? coachLL.setAsideWeakness(params) : coachLL.setAsidePlateau(params),
      canBringBack: true,
    };
  }
}

/** one day of the Next Session Only block (#6428) */
export interface NextSessionRow {
  level: IntensityLevel;
  /** the day's name; none for a one-target exercise */
  label: TranslatedString | undefined;
  /** set on the day that comes next */
  comesNextTooltip?: TranslatedString;
  weight_kg: number | undefined;
  minReps: number | undefined;
  minRepsError?: TranslatedString;
}

/** what the coach's parts of the page show (#6428) */
export interface CoachView {
  schemeOptions: { scheme: IntensityScheme; label: TranslatedString }[];
  scheme: IntensityScheme;
  /** a rebuild is pending: the next session is a strength test */
  pendingTest: boolean;
  historyUnreadable: boolean;
  nextRows: NextSessionRow[];
  sourceNote: TranslatedString | undefined;
  strengthRows: { day: TranslatedString | undefined; text: TranslatedString }[];
  questionCards: { type: 'warning' | 'success'; message: TranslatedString }[];
  setAside: { text: TranslatedString; canBringBack: boolean } | undefined;
  changes: { date: string; text: TranslatedString }[];
}
